'use client'
/**
 * Customer login / signup (Persian/RTL). Email+password with a login⇄signup
 * toggle, plus "ورود با گوگل" which hands off to the server-side OAuth start
 * endpoint (a full-page redirect, not fetch, so Google's consent flow works).
 *
 * Mirrors the admin login styling but for shoppers; on success we route to
 * /account (or the ?next= target).
 */
import { Suspense, useState } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import Wordmark from '@/components/ui/Wordmark'
import { useLanguage } from '@/lib/context/LanguageContext'

function LoginInner() {
  const router = useRouter()
  const params = useSearchParams()
  const { t } = useLanguage()
  const next = params.get('next') || '/account'

  // 'form' = login/signup fields; 'verify' = the emailed-code step after signup.
  const [step, setStep] = useState<'form' | 'verify'>('form')
  const [mode, setMode] = useState<'login' | 'signup'>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setInfo(null)
    setSubmitting(true)
    try {
      if (step === 'verify') {
        // Confirm the emailed code → creates the account and logs in.
        const res = await fetch('/api/customer-auth/verify-email', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ email, code }),
        })
        if (!res.ok) {
          setError(t.shop.verifyError)
          return
        }
        router.push(next)
        router.refresh()
        return
      }

      const path = mode === 'login' ? '/api/customer-auth/login' : '/api/customer-auth/signup'
      const body =
        mode === 'login'
          ? { email, password }
          : { email, password, full_name: fullName || undefined, phone: phone || undefined }
      const res = await fetch(path, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!res.ok) {
        setError(mode === 'login' ? t.shop.loginError : t.shop.signupError)
        return
      }
      // Signup now creates the account and logs in immediately (no email
      // verification step) — same as login from here on. Email can be verified
      // later, optionally, from the account panel.
      router.push(next)
      router.refresh()
    } catch {
      setError(step === 'verify' ? t.shop.verifyError : t.shop.signupError)
    } finally {
      setSubmitting(false)
    }
  }

  async function onResend() {
    setError(null)
    setInfo(null)
    setSubmitting(true)
    try {
      const res = await fetch('/api/customer-auth/resend-code', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email }),
      })
      setInfo(res.ok ? t.shop.resendDone : t.shop.resendWait)
    } catch {
      setError(t.shop.signupError)
    } finally {
      setSubmitting(false)
    }
  }

  const googleHref = `/api/customer-auth/google/start?next=${encodeURIComponent(next)}`

  return (
    <main dir="rtl" className="flex min-h-[100svh] items-center justify-center bg-surface px-6">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm rounded-[var(--radius-card)] border border-line bg-surface p-8 shadow-[0_30px_60px_-30px_rgba(26,26,23,0.18)]"
      >
        <div className="flex flex-col items-center">
          <Wordmark className="text-2xl" />
          <p className="mt-2 text-sm font-semibold text-ink">
            {step === 'verify'
              ? t.shop.verifyTitle
              : mode === 'login'
                ? t.shop.loginTitle
                : t.shop.signupTitle}
          </p>
        </div>

        {step === 'verify' ? (
          <>
            <p className="mt-6 text-xs leading-relaxed text-ink-muted">{t.shop.verifySub}</p>
            <p dir="ltr" className="mt-1 text-right text-sm font-semibold text-ink">
              {email}
            </p>

            <label className="mt-5 flex flex-col">
              <span className="mb-2 text-xs text-ink-muted">{t.shop.codeLabel}</span>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                dir="ltr"
                required
                pattern="\d{6}"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full rounded-md border border-line bg-surface px-4 py-3 text-center text-lg tracking-[0.5em] text-ink focus:border-blue focus:outline-none focus:ring-2 focus:ring-blue/25 transition"
              />
            </label>
          </>
        ) : (
          <>
            {/* Google */}
            <a
              href={googleHref}
              className="mt-8 flex w-full items-center justify-center gap-2 rounded-full border border-line bg-surface py-3 text-sm font-semibold text-ink hover:border-accent transition-colors"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
                <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.71-1.57 2.68-3.89 2.68-6.62Z" />
                <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.34A9 9 0 0 0 9 18Z" />
                <path fill="#FBBC05" d="M3.97 10.72A5.4 5.4 0 0 1 3.68 9c0-.6.1-1.18.29-1.72V4.94H.96A9 9 0 0 0 0 9c0 1.45.35 2.82.96 4.06l3.01-2.34Z" />
                <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.59C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.94l3.01 2.34C4.68 5.16 6.66 3.58 9 3.58Z" />
              </svg>
              {t.shop.googleSignIn}
            </a>

            <div className="my-5 flex items-center gap-3 text-xs text-ink-muted">
              <span className="h-px flex-1 bg-line" />
              {t.shop.or}
              <span className="h-px flex-1 bg-line" />
            </div>

            {mode === 'signup' && (
              <label className="mb-4 flex flex-col">
                <span className="mb-2 text-xs text-ink-muted">{t.shop.fullName}</span>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full rounded-md border border-line bg-surface px-4 py-3 text-sm text-ink focus:border-blue focus:outline-none focus:ring-2 focus:ring-blue/25 transition"
                />
              </label>
            )}

            <label className="flex flex-col">
              <span className="mb-2 text-xs text-ink-muted">{t.shop.email}</span>
              <input
                type="email"
                autoComplete="username"
                dir="ltr"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-md border border-line bg-surface px-4 py-3 text-sm text-ink focus:border-blue focus:outline-none focus:ring-2 focus:ring-blue/25 transition"
              />
            </label>

            <label className="mt-4 flex flex-col">
              <span className="mb-2 text-xs text-ink-muted">{t.shop.password}</span>
              <input
                type="password"
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                required
                minLength={mode === 'signup' ? 8 : undefined}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-md border border-line bg-surface px-4 py-3 text-sm text-ink focus:border-blue focus:outline-none focus:ring-2 focus:ring-blue/25 transition"
              />
            </label>

            {mode === 'signup' && (
              <label className="mt-4 flex flex-col">
                <span className="mb-2 text-xs text-ink-muted">{t.shop.phone}</span>
                <input
                  type="tel"
                  dir="ltr"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full rounded-md border border-line bg-surface px-4 py-3 text-sm text-ink focus:border-blue focus:outline-none focus:ring-2 focus:ring-blue/25 transition"
                />
              </label>
            )}
          </>
        )}

        {error && (
          <p className="mt-4 rounded-md border border-accent/30 bg-accent-soft/40 px-3 py-2 text-xs text-accent-deep">
            {error}
          </p>
        )}
        {info && (
          <p className="mt-4 rounded-md border border-line bg-surface px-3 py-2 text-xs text-ink-muted">
            {info}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="mt-6 w-full rounded-full bg-ink py-3 text-sm font-semibold text-surface hover:bg-accent disabled:opacity-60 transition-colors"
        >
          {step === 'verify'
            ? t.shop.verifySubmit
            : mode === 'login'
              ? t.shop.loginSubmit
              : t.shop.signupSubmit}
        </button>

        {step === 'verify' ? (
          <div className="mt-4 flex items-center justify-between text-xs">
            <button
              type="button"
              onClick={onResend}
              disabled={submitting}
              className="text-ink-muted hover:text-ink disabled:opacity-60 transition-colors"
            >
              {t.shop.resendCode}
            </button>
            <button
              type="button"
              onClick={() => {
                setStep('form')
                setCode('')
                setError(null)
                setInfo(null)
              }}
              className="text-ink-muted hover:text-ink transition-colors"
            >
              {t.shop.changeEmail}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => {
              setMode((m) => (m === 'login' ? 'signup' : 'login'))
              setError(null)
            }}
            className="mt-4 w-full text-center text-xs text-ink-muted hover:text-ink transition-colors"
          >
            {mode === 'login' ? t.shop.noAccount : t.shop.haveAccount}
          </button>
        )}
      </form>
    </main>
  )
}

export default function LoginPage() {
  return (
    <Suspense>
      <LoginInner />
    </Suspense>
  )
}
